import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import Script from 'next/script'

import { readEffectiveConsent } from '@ui/lib/consent'
import { marketingUrl } from '@ui/lib/urls'

import type { ClientSessionData } from '@web-app/hoc/withSession'
import type {
  Branch,
  BranchError,
  BranchEventProperties,
  BranchLinkData,
  BranchLinkDataType,
  BranchReferringData,
} from '@web-app/types'
import {
  createBranchLinkData,
  createDeepLinkPath,
  extractAttributionData,
} from '@web-app/utils/branchAttribution'
import { detectPlatform } from '@web-app/utils/device'

interface BranchContextType {
  publishBranchEvent: (
    eventName: string,
    eventProperties?: BranchEventProperties,
  ) => void
  trackPageView: () => void
  createAttributedDownloadLink: (
    platform?: 'ios' | 'android' | 'auto',
    additionalData?: BranchLinkData,
  ) => Promise<string>
  // A referral invite link; null when Branch can't mint one.
  createReferralLink: (code: string) => Promise<string | null>
  isInitialized: boolean
  branchData: BranchReferringData | null
  setBranchViewData: (data: BranchLinkData) => Promise<void>
  error: string | null
}

export const BranchContext = createContext<BranchContextType | undefined>(
  undefined,
)

const BRANCH_KEY = process.env.NEXT_PUBLIC_BRANCH_KEY

export const BranchProvider: React.FC<{
  children: React.ReactNode
  sessionData?: ClientSessionData
}> = ({ children, sessionData }) => {
  const user = sessionData?.user

  const branchRef = useRef<Branch | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [branchData, setBranchData] = useState<BranchReferringData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const eventQueueRef = useRef<
    Array<{ eventName: string; eventProperties?: BranchEventProperties }>
  >([])

  // Set user details with error handling
  const setUserDetails = useCallback(() => {
    if (!branchRef.current || !user?.id) return

    try {
      branchRef.current.setIdentity(user.id, (err, data) => {
        if (err) {
          console.error('Error: Could not set Branch identity:', err)
        } else {
          console.log('Branch identity set successfully:', data)
        }
      })
    } catch (err) {
      console.error('Error setting Branch user details:', err)
    }
  }, [user])

  // Function to get Branch data and store it with retry logic
  const getBranchData = useCallback(() => {
    if (!branchRef.current || !isInitialized) return

    const attemptDataRetrieval = (retryCount = 0) => {
      const maxRetries = 2

      if (!branchRef.current) return

      branchRef.current.data(
        (err: BranchError, data: Record<string, unknown>) => {
          if (err) {
            console.error('Error getting Branch data:', err)

            // Retry on failure
            if (retryCount < maxRetries) {
              console.log(
                `Retrying Branch data retrieval (attempt ${retryCount + 1}/${maxRetries})`,
              )
              setTimeout(() => attemptDataRetrieval(retryCount + 1), 1000)
            }
            return
          }

          setBranchData(data as BranchReferringData)
          console.log('Branch referring data retrieved:', data)

          // Clear any previous errors on successful data retrieval
          if (error) {
            setError(null)
          }
        },
      )
    }

    attemptDataRetrieval()
  }, [isInitialized, error])

  // First run from the SDK script's onLoad; runs again when the identity
  // metadata changes, which Branch treats as a re-init with the new options.
  const initializeBranch = useCallback(() => {
    if (!BRANCH_KEY) return

    window.branch.init(
      BRANCH_KEY,
      {
        logging_enabled: process.env.NODE_ENV === 'development',
        metadata: {
          web_session_id: sessionData?.sessionId,
          user_id: user?.id,
        },
        tracking_disabled: !readEffectiveConsent().advertising,
      },
      (err: BranchError, data: Record<string, unknown>) => {
        if (err) {
          const errorMessage = `Branch initialization failed: ${err.message || 'Unknown error'}`
          console.warn(errorMessage)
          setError(errorMessage)
          return
        }

        console.log('Branch SDK initialized successfully:', data)

        branchRef.current = window.branch
        setIsInitialized(true)
        setError(null)

        // Process queued events
        eventQueueRef.current.forEach(({ eventName, eventProperties }) => {
          branchRef.current?.logEvent(eventName, eventProperties)
        })
        eventQueueRef.current = []
      },
    )
  }, [sessionData?.sessionId, user?.id])

  useEffect(() => {
    if (branchRef.current) initializeBranch()
  }, [initializeBranch])

  // Update user identity when user data changes
  useEffect(() => {
    if (isInitialized) {
      setUserDetails()
      // Refresh Branch data when user changes
      getBranchData()
    }
  }, [user, setUserDetails, getBranchData, isInitialized])

  // Set Branch view data for dynamic deep linking
  const setBranchViewData = useCallback(
    (data: BranchLinkData): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (!branchRef.current || !isInitialized) {
          console.warn(
            'Warning: Branch SDK not initialized, skipping view data setting',
          )
          reject(new Error('Branch SDK not initialized')) // Reject instead of resolving
          return
        }

        branchRef.current.setBranchViewData(data, (err: BranchError) => {
          if (err) {
            console.warn('Warning setting Branch view data:', err)
            reject(err)
          } else {
            resolve()
          }
        })
      })
    },
    [isInitialized],
  )

  // Create attributed download link with session attribution data
  const createAttributedDownloadLink = useCallback(
    async (
      platform: 'ios' | 'android' | 'auto' = 'auto',
      additionalData: BranchLinkData = {},
    ): Promise<string> => {
      const fallbackUrls = {
        ios: 'https://apps.apple.com/us/app/atm-instant-cash-rewards/id1507829573',
        android:
          'https://play.google.com/store/apps/details?id=com.atm.atm.prod',
      }

      const resolvePlatform = (
        p: 'ios' | 'android' | 'auto',
      ): 'ios' | 'android' => (p === 'auto' ? detectPlatform() : p)

      if (!branchRef.current || !isInitialized) {
        // Fallback to regular app store links if Branch is not available
        const finalPlatform = resolvePlatform(platform)
        console.log(
          'Branch not initialized, using fallback URL for',
          finalPlatform,
        )
        return fallbackUrls[finalPlatform]
      }

      try {
        // Extract attribution data from session
        const sessionAttributionData = extractAttributionData(
          sessionData?.utmParams ?? undefined,
          sessionData?.sessionId ?? undefined,
          sessionData?.browserFingerprint ?? undefined,
          user?.id,
          sessionData?.mcid ?? undefined,
        )

        // Merge with Branch referring data if available
        const branchReferringData = branchData?.data_parsed || {}
        const referringLink = branchData?.['~referring_link']

        // Create comprehensive attribution data
        const combinedAttributionData = {
          ...sessionAttributionData,
          // Include original Branch attribution data
          ...branchReferringData,
          // Preserve referring link information
          original_referring_link: referringLink,
          // Add Branch identity if available
          referring_identity: branchData?.referring_identity,
          // Indicate this is a web-to-app conversion
          web_to_app_conversion: true,
          // Add platform information
          target_platform: resolvePlatform(platform),
        }

        // Create deep link path
        const deepLinkPath = createDeepLinkPath('complete-registration')

        // Prepare link data with comprehensive attribution
        const linkData = createBranchLinkData(
          combinedAttributionData,
          deepLinkPath,
          additionalData,
        )

        // Set the view data first to ensure it's captured in the link with timeout
        try {
          await Promise.race([
            setBranchViewData(linkData),
            new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error('setBranchViewData timeout')),
                3000,
              ),
            ),
          ])
        } catch (viewErr) {
          console.warn('Warning: Could not set Branch view data:', viewErr)
          // Continue to create link even if view data setting failed
        }

        // Create the Branch link with timeout using Promise wrapper
        return new Promise<string>((resolve) => {
          const linkTimeout = setTimeout(() => {
            const finalUrl = fallbackUrls[resolvePlatform(platform)]
            console.log(
              'Branch link creation timed out, using fallback:',
              finalUrl,
            )
            resolve(finalUrl)
          }, 5000)

          if (!branchRef.current) {
            clearTimeout(linkTimeout)
            resolve(fallbackUrls[resolvePlatform(platform)])
            return
          }

          branchRef.current.link(linkData, (err: BranchError, url?: string) => {
            clearTimeout(linkTimeout)

            if (err) {
              console.warn('Warning creating Branch link:', err)
              const finalUrl = url || fallbackUrls[resolvePlatform(platform)]
              resolve(finalUrl)
            } else {
              const finalUrl = url || fallbackUrls[resolvePlatform(platform)]
              console.log('Successfully created Branch link:', finalUrl)
              resolve(finalUrl)
            }
          })
        })
      } catch (err) {
        console.warn('Error in createAttributedDownloadLink:', err)
        return fallbackUrls[resolvePlatform(platform)]
      }
    },
    [sessionData, user, branchData, isInitialized],
  )

  // The mobile apps' referral link, payload for payload (DynamicLinkFactory
  // on iOS, DynamicLinkHelper on Android): `deeplink` is the control param
  // the app reads to open its referral screen with the code.
  const createReferralLink = useCallback(
    (code: string): Promise<string | null> => {
      if (!branchRef.current || !isInitialized) return Promise.resolve(null)
      const linkData: BranchLinkDataType = {
        canonicalIdentifier: 'branchLinks',
        feature: 'referrals',
        data: {
          deeplink: `atmapp://app_navigator/?nav=referral&referral_code=${encodeURIComponent(code)}`,
          $ios_nativelink: 'true',
          $android_deepview: 'false',
          $fallback_url: marketingUrl('/download'),
          $desktop_url: marketingUrl('/download'),
        },
      }
      return new Promise((resolve) => {
        const timeout = setTimeout(() => resolve(null), 5000)
        branchRef.current?.link(linkData, (err, url) => {
          clearTimeout(timeout)
          resolve(err || !url ? null : url)
        })
      })
    },
    [isInitialized],
  )

  // Publish Branch event with error handling
  const publishBranchEvent = useCallback(
    (eventName: string, eventProperties?: BranchEventProperties): void => {
      if (isInitialized && branchRef.current) {
        try {
          branchRef.current.logEvent(
            eventName,
            eventProperties,
            (err: BranchError) => {
              if (err) {
                console.warn(
                  `Warning logging Branch event '${eventName}':`,
                  err,
                )
              }
            },
          )
        } catch (err) {
          console.warn(`Error publishing Branch event '${eventName}':`, err)
        }
      } else {
        // Queue the event to be sent after initialization
        eventQueueRef.current.push({ eventName, eventProperties })
      }
    },
    [isInitialized],
  )

  // Track page view with error handling
  const trackPageView = useCallback((): void => {
    if (isInitialized && branchRef.current) {
      try {
        branchRef.current.track('pageview', {}, (err: BranchError) => {
          if (err) {
            console.warn('Warning tracking Branch pageview:', err)
          }
        })
      } catch (err) {
        console.warn('Error tracking Branch pageview:', err)
      }
    } else {
      // Queue the pageview event to be sent after initialization
      eventQueueRef.current.push({ eventName: 'pageview', eventProperties: {} })
    }
  }, [isInitialized])

  return (
    <BranchContext.Provider
      value={{
        publishBranchEvent,
        trackPageView,
        createAttributedDownloadLink,
        createReferralLink,
        isInitialized,
        branchData,
        setBranchViewData,
        error,
      }}
    >
      {BRANCH_KEY && (
        <Script
          src="https://cdn.branch.io/branch-latest.min.js"
          onLoad={initializeBranch}
        />
      )}
      {children}
    </BranchContext.Provider>
  )
}

export const useBranch = () => {
  const context = useContext(BranchContext)
  if (context === undefined) {
    throw new Error('useBranch must be used within a BranchProvider')
  }
  return context
}
